import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const CreateTransaction = () => {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    type: '',
    duration: '',
    expireDays: '',
    gst: '',
    paymentId: '',
    orderId: '',
    description: ''
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!vendorId) {
      toast.error('Vendor id is missing');
      return;
    }

    if (!formData.amount || !formData.type || formData.expireDays === '') {
      toast.error('Amount, transaction type, and expire days are required');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        userId: vendorId,
        amount: Number(formData.amount),
        type: formData.type,
        description: formData.description || '',
        duration: formData.duration === '' ? undefined : Number(formData.duration),
        expireDays: Number(formData.expireDays),
        gst: formData.gst === '' ? undefined : Number(formData.gst),
        paymentId: formData.paymentId || undefined,
        orderId: formData.orderId || undefined
      };

      const response = await apiService.post('createManualWalletTransaction', payload);

      if (response?.status) {
        toast.success(response.message || 'Transaction created successfully');
        setFormData({
          amount: '',
          type: '',
          duration: '',
          expireDays: '',
          gst: '',
          paymentId: '',
          orderId: '',
          description: ''
        });
      } else {
        toast.error(response?.message || 'Failed to create transaction');
      }
    } catch (error) {
      console.error('Create transaction error:', error);
      toast.error('Something went wrong, try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-50 p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add Manual Transaction</h1>
              <p className="text-sm text-gray-600 mt-1">Vendor ID: {vendorId}</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="Enter Amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (Days)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', e.target.value)}
                  placeholder="Enter Duration in Days"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expire Days</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.expireDays}
                  onChange={(e) => handleChange('expireDays', e.target.value)}
                  placeholder="Enter Expire Days"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.gst}
                  onChange={(e) => handleChange('gst', e.target.value)}
                  placeholder="Enter GST Amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment ID</label>
                <input
                  type="text"
                  value={formData.paymentId}
                  onChange={(e) => handleChange('paymentId', e.target.value)}
                  placeholder="Enter Payment ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order ID</label>
                <input
                  type="text"
                  value={formData.orderId}
                  onChange={(e) => handleChange('orderId', e.target.value)}
                  placeholder="Enter Order ID (Optional - Auto generated if empty)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Enter transaction description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center"
              >
                {submitting && (
                  <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-r-transparent rounded-full mr-2"></span>
                )}
                {submitting ? 'Please wait...' : 'Create Transaction'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTransaction;
